import {useEffect, useMemo, useState} from 'react'
import {useOverlayStore} from '@app/common/stores'
import {Modal, ModalFooter} from '@app/components/Modal/Modal'
import {api} from '@app/hooks/useApi'
import {SmallHeading} from '@app/components/Modal/SmallHeading'
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Input,
  ModalBody,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Radio,
  RadioGroup,
  Stack,
  Text,
  IconButton,
  Flex,
  Box,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Checkbox,
  VStack,
  HStack,
} from '@chakra-ui/react'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {toast} from 'react-hot-toast'
import {MinusIcon, PlusIcon, QuestionMarkCircleIcon} from '@heroicons/react/24/solid'
import {useTournament} from '@app/common/api'
import {Select as ChakraMultiSelect, Select} from 'chakra-react-select'
import {formatDistance} from 'date-fns'
import type {ActionMeta, GroupBase, MultiValue} from 'chakra-react-select'

// Type definitions
interface Cosmetic {
  id: number
  category: string
  categoryId: number
  name: string
}

interface CosmeticOption {
  label: string
  value: {
    id: number
    categoryId: number
  }
}

interface LeaderboardPlayer {
  player: string
  [key: string]: any
}

type LeaderboardData = LeaderboardPlayer[]

interface TournamentRound {
  value: number
  label: string
  after: number
  before: number
}

interface TournamentRoundOption {
  label: string
  value: number
}

interface GiveCosmeticsParams {
  players: string[]
  cosmetics: Array<{id: number; categoryId: number}>
  dryRun: boolean
}

interface DryRunResult {
  affectedPlayers: string[]
}

/**
 * Hook to fetch available cosmetics from the server
 */
function useCosmetics() {
  return useQuery<Cosmetic[]>({
    queryKey: ['admin', 'cosmetics'],
    queryFn: async () => {
      const {data} = await api.get<Cosmetic[]>('/admin/cosmetics')
      return data
    },
  })
}

/**
 * Hook to fetch tournament leaderboard data for a specific round
 */
function useTournamentLeaderboard(round: number | null, enabled = true) {
  return useQuery<LeaderboardData>({
    queryKey: ['tournament', 'leaderboard', round],
    queryFn: async () => {
      // Force bypassAnonymous to true to see real player names
      const {data} = await api.get<LeaderboardData>('/leaderboard', {
        params: {
          type: 'tournament',
          round,
          bypassAnonymous: true,
          limit: 100,
        },
      })
      return data
    },
    enabled: enabled && round !== null && round !== undefined,
  })
}

/**
 * Hook to send requests to give cosmetics to players
 */
function useGiveCosmetics() {
  const queryClient = useQueryClient()

  return useMutation<DryRunResult, Error, GiveCosmeticsParams>({
    mutationFn: async ({players, cosmetics, dryRun}) => {
      const {data} = await api.post<DryRunResult>('/admin/cosmetics', {
        players,
        cosmetics,
        dryRun,
      })
      return data
    },
    onSuccess: () => {
      // Invalidate relevant queries after successful operation
      queryClient.invalidateQueries({queryKey: ['admin', 'cosmetics']})
    },
  })
}

export default function AdminGiveCosmeticsModal() {
  const [adminGiveCosmeticsOpen, setAdminGiveCosmeticsOpen] = useOverlayStore(state => [
    state.adminGiveCosmeticsOpen,
    state.setAdminGiveCosmeticsOpen,
  ])
  const {isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose} = useDisclosure()
  const [selectedMode, setSelectedMode] = useState<'manual' | 'leaderboard'>('manual')
  const [selectedCosmetics, setSelectedCosmetics] = useState<CosmeticOption[]>([])
  const [playerInputs, setPlayerInputs] = useState<string[]>([''])
  const [topPlayerCount, setTopPlayerCount] = useState<number>(10)
  const [selectedRound, setSelectedRound] = useState<number | null>(null)
  const [confirmationText, setConfirmationText] = useState<string>('')
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null)
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set())

  const {data: tournament} = useTournament()
  const {data: cosmetics, isLoading: isLoadingCosmetics} = useCosmetics()
  const {data: leaderboardData, isLoading: isLoadingLeaderboard} = useTournamentLeaderboard(
    selectedRound,
    selectedMode === 'leaderboard' && selectedRound !== null,
  )
  const giveCosmetics = useGiveCosmetics()

  // Calculate available rounds
  const availableRounds = useMemo<TournamentRound[]>(() => {
    if (!tournament) return []

    const currentTime = Math.floor(Date.now() / 1000)
    const elapsed = currentTime - Math.floor(new Date(tournament.startTime).getTime() / 1000)
    const maxRound = Math.min(Math.floor(elapsed / tournament.roundDuration) + 1, tournament.roundCount)
    const hasEnded = currentTime > Math.floor(new Date(tournament.endTime).getTime() / 1000)
    const lastAvailableRound = hasEnded ? tournament.roundCount : maxRound

    return Array.from({length: lastAvailableRound}, (_, i) => {
      const roundNumber = i + 1
      const after =
        Math.floor(new Date(tournament.startTime).getTime() / 1000) + (roundNumber - 1) * tournament.roundDuration
      const before = after + tournament.roundDuration

      return {
        value: roundNumber,
        label: `Round ${roundNumber} (${formatDistance(after * 1000, before * 1000, {addSuffix: false})})`,
        after,
        before,
      }
    })
  }, [tournament])

  // Convert availableRounds to options for chakra-react-select
  const tournamentRoundOptions = useMemo<TournamentRoundOption[]>(() => {
    return availableRounds.map(round => ({
      label: round.label,
      value: round.value,
    }))
  }, [availableRounds])

  // Get the current selected option for the Select component
  const selectedRoundOption = useMemo<TournamentRoundOption | null>(() => {
    if (!selectedRound) return null
    const found = availableRounds.find(r => r.value === selectedRound)
    return found ? {label: found.label, value: found.value} : null
  }, [selectedRound, availableRounds])

  // Set default selected round
  useEffect(() => {
    if (availableRounds.length > 0 && !selectedRound) {
      setSelectedRound(availableRounds[availableRounds.length - 1]?.value || null)
    }
  }, [availableRounds, selectedRound])

  // Initialize selected players when leaderboard data changes
  useEffect(() => {
    if (selectedMode === 'leaderboard' && leaderboardData) {
      const newSelectedPlayers = new Set<string>()
      leaderboardData.slice(0, topPlayerCount).forEach(player => {
        newSelectedPlayers.add(player.player)
      })
      setSelectedPlayers(newSelectedPlayers)
    }
  }, [leaderboardData, topPlayerCount, selectedMode])

  // Update selected players when top player count changes
  useEffect(() => {
    if (selectedMode === 'leaderboard' && leaderboardData) {
      const newSelectedPlayers = new Set<string>(selectedPlayers)
      leaderboardData.slice(0, topPlayerCount).forEach(player => {
        newSelectedPlayers.add(player.player)
      })
      setSelectedPlayers(newSelectedPlayers)
    }
  }, [topPlayerCount, leaderboardData, selectedMode])

  // Group cosmetics by category
  const cosmeticOptions = useMemo<GroupBase<CosmeticOption>[]>(() => {
    if (!cosmetics) return []

    const grouped: Record<string, CosmeticOption[]> = cosmetics.reduce(
      (acc: Record<string, CosmeticOption[]>, item) => {
        if (!acc[item.category]) {
          acc[item.category] = []
        }
        acc[item.category]!.push({
          // Include category in the label for better identification
          label: `${item.name} (${item.category})`,
          value: {id: item.id, categoryId: item.categoryId},
        })
        return acc
      },
      {},
    )

    return Object.entries(grouped).map(([category, options]) => ({
      label: category,
      options,
    }))
  }, [cosmetics])

  // Get selected players based on mode
  const getSelectedPlayers = (): string[] => {
    if (selectedMode === 'manual') {
      return playerInputs.filter(p => p.trim() !== '')
    }
    return Array.from(selectedPlayers)
  }

  // Toggle player selection
  const togglePlayerSelection = (player: string) => {
    const newSelectedPlayers = new Set(selectedPlayers)
    if (newSelectedPlayers.has(player)) {
      newSelectedPlayers.delete(player)
    } else {
      newSelectedPlayers.add(player)
    }
    setSelectedPlayers(newSelectedPlayers)
  }

  // Select/deselect all players
  const toggleAllPlayers = (selected: boolean) => {
    if (!leaderboardData) return

    const newSelectedPlayers = new Set<string>()
    if (selected) {
      leaderboardData.slice(0, topPlayerCount).forEach(player => {
        newSelectedPlayers.add(player.player)
      })
    }
    setSelectedPlayers(newSelectedPlayers)
  }

  // Add player input field
  const addPlayerInput = () => {
    setPlayerInputs([...playerInputs, ''])
  }

  // Remove player input field
  const removePlayerInput = (index: number) => {
    const newInputs = [...playerInputs]
    newInputs.splice(index, 1)
    if (newInputs.length === 0) {
      newInputs.push('')
    }
    setPlayerInputs(newInputs)
  }

  // Update player input
  const updatePlayerInput = (index: number, value: string) => {
    const newInputs = [...playerInputs]
    newInputs[index] = value
    setPlayerInputs(newInputs)
  }

  // Handle dry run
  const handleDryRun = async () => {
    const players = getSelectedPlayers()
    const cosmetics = selectedCosmetics.map(item => item.value)

    if (players.length === 0) {
      toast.error('No players selected')
      return
    }

    if (cosmetics.length === 0) {
      toast.error('No cosmetics selected')
      return
    }

    try {
      const result = await giveCosmetics.mutateAsync({
        players,
        cosmetics,
        dryRun: true,
      })

      setDryRunResult(result)
      onConfirmOpen()
    } catch (error) {
      toast.error('Failed to prepare cosmetics distribution')
    }
  }

  // Handle final submit
  const handleSubmit = async () => {
    if (confirmationText !== 'i confirm') {
      toast.error('Please type "i confirm" to proceed')
      return
    }

    if (!dryRunResult) {
      toast.error('Missing dry run results')
      return
    }

    try {
      await giveCosmetics.mutateAsync({
        players: dryRunResult.affectedPlayers,
        cosmetics: selectedCosmetics.map(item => item.value),
        dryRun: false,
      })

      toast.success('Cosmetics distributed successfully')
      onConfirmClose()
      setAdminGiveCosmeticsOpen(false)
    } catch (error) {
      toast.error('Failed to distribute cosmetics')
    }
  }

  // Reset when modal closes
  const handleClose = () => {
    setSelectedMode('manual')
    setSelectedCosmetics([])
    setPlayerInputs([''])
    setTopPlayerCount(10)
    setSelectedRound(null)
    setConfirmationText('')
    setDryRunResult(null)
    setSelectedPlayers(new Set())
    setAdminGiveCosmeticsOpen(false)
  }

  // Format cosmetic names for display
  const formatCosmeticNames = (): string => {
    return selectedCosmetics.map(option => option.label).join(', ')
  }

  // Handle multi-select change
  const handleCosmeticsChange = (newValue: MultiValue<CosmeticOption>, _actionMeta: ActionMeta<CosmeticOption>) => {
    setSelectedCosmetics(newValue as CosmeticOption[])
  }

  return (
    <Modal header="Give Cosmetics" isOpen={adminGiveCosmeticsOpen} onClose={handleClose}>
      <ModalBody as={Stack} gap="16px" px={0} py={4}>
        {/* Mode Selection */}
        <FormControl>
          <SmallHeading mb={3}>Selection Mode</SmallHeading>
          <RadioGroup
            onChange={(value: string) => setSelectedMode(value as 'manual' | 'leaderboard')}
            value={selectedMode}
          >
            <Stack direction="row" spacing={6}>
              <Radio value="manual">Manual Player Selection</Radio>
              <Radio value="leaderboard">Tournament Leaderboard</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <Divider />

        {/* Cosmetics Selection */}
        <FormControl>
          <SmallHeading mb={3}>Cosmetics</SmallHeading>
          <ChakraMultiSelect<CosmeticOption, true, GroupBase<CosmeticOption>>
            isMulti
            options={cosmeticOptions}
            placeholder="Select cosmetics..."
            onChange={handleCosmeticsChange}
            value={selectedCosmetics}
            isLoading={isLoadingCosmetics}
            size="sm"
          />
          <FormHelperText fontSize="xs" mt={2}>
            Select the cosmetics to give to players. Grouped by category.
          </FormHelperText>
        </FormControl>

        <Divider />

        {/* Manual Player Input */}
        {selectedMode === 'manual' && (
          <FormControl>
            <SmallHeading mb={3}>Players</SmallHeading>
            <VStack spacing={3} align="stretch">
              {playerInputs.map((player, index) => (
                <Flex key={index} gap={2} w="full">
                  <Input
                    value={player}
                    onChange={e => updatePlayerInput(index, e.target.value)}
                    placeholder="Enter player name or XUID"
                    size="sm"
                    flexGrow={1}
                    borderRadius="md"
                  />
                  {playerInputs.length > 1 && (
                    <IconButton
                      icon={<MinusIcon height={16} width={16} />}
                      size="sm"
                      onClick={() => removePlayerInput(index)}
                      aria-label="Remove player"
                      borderRadius="md"
                    />
                  )}
                  {index === playerInputs.length - 1 && (
                    <IconButton
                      icon={<PlusIcon height={16} width={16} />}
                      size="sm"
                      onClick={addPlayerInput}
                      aria-label="Add player"
                      borderRadius="md"
                    />
                  )}
                </Flex>
              ))}
            </VStack>
            <FormHelperText fontSize="xs" mt={2}>
              Enter player names or XUIDs. Click + to add more players.
            </FormHelperText>
          </FormControl>
        )}

        {/* Tournament Leaderboard Selection */}
        {selectedMode === 'leaderboard' && (
          <VStack spacing={4} align="stretch">
            <FormControl>
              <SmallHeading mb={3}>Tournament Round</SmallHeading>
              <Select<TournamentRoundOption, false, GroupBase<TournamentRoundOption>>
                className="react-select"
                classNamePrefix="react-select"
                onChange={option => setSelectedRound(option?.value || null)}
                options={tournamentRoundOptions}
                placeholder="Select round..."
                isDisabled={availableRounds.length === 0}
                value={selectedRoundOption}
                size="sm"
                menuPortalTarget={document.body}
                styles={{menuPortal: provided => ({...provided, zIndex: 9999})}}
                isClearable={false}
              />
              <FormHelperText fontSize="xs" mt={2}>
                Select which tournament round to use for player selection.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <SmallHeading mb={3}>Top Players</SmallHeading>
              <NumberInput
                min={1}
                max={100}
                value={topPlayerCount}
                onChange={(_, num) => setTopPlayerCount(num)}
                size="sm"
                borderRadius="md"
              >
                <NumberInputField borderRadius="md" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs" mt={2}>
                Select how many top players from the leaderboard will receive the cosmetics.
              </FormHelperText>
            </FormControl>

            {isLoadingLeaderboard && <Text fontSize="sm">Loading leaderboard data...</Text>}

            {!isLoadingLeaderboard && leaderboardData && (
              <Box maxH="200px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                <HStack justify="space-between" mb={3}>
                  <Text fontSize="sm" fontWeight="bold">
                    Players ({selectedPlayers.size} selected)
                  </Text>
                  <HStack spacing={4}>
                    <Button size="xs" onClick={() => toggleAllPlayers(true)} variant="outline">
                      Select All
                    </Button>
                    <Button size="xs" onClick={() => toggleAllPlayers(false)} variant="outline">
                      Deselect All
                    </Button>
                  </HStack>
                </HStack>

                {leaderboardData && leaderboardData.length > 0 ? (
                  <VStack align="stretch" spacing={1}>
                    {leaderboardData.slice(0, topPlayerCount).map((player, idx: number) => (
                      <Checkbox
                        key={idx}
                        isChecked={selectedPlayers.has(player.player)}
                        onChange={() => togglePlayerSelection(player.player)}
                        size="sm"
                      >
                        <Text fontSize="sm">
                          {idx + 1}. {player.player}
                        </Text>
                      </Checkbox>
                    ))}
                  </VStack>
                ) : (
                  <Flex direction="column" align="center" justify="center" py={4}>
                    <QuestionMarkCircleIcon color="var(--chakra-colors-orange-200)" height={50} width={50} />
                    <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
                      No leaderboard data available for this round
                    </Text>
                  </Flex>
                )}
              </Box>
            )}
          </VStack>
        )}

        <Divider />

        {/* Summary & Action */}
        <Box>
          <SmallHeading mb={3}>Summary</SmallHeading>
          <VStack spacing={2} align="stretch">
            <Text fontSize="sm">
              Cosmetics: {selectedCosmetics.length > 0 ? formatCosmeticNames() : 'None selected'}
            </Text>
            <Text fontSize="sm">
              Players: {getSelectedPlayers().length > 0 ? `${getSelectedPlayers().length} selected` : 'None selected'}
            </Text>
          </VStack>
        </Box>

        <Button
          onClick={handleDryRun}
          isLoading={giveCosmetics.isPending}
          isDisabled={selectedCosmetics.length === 0 || getSelectedPlayers().length === 0}
          size="sm"
          colorScheme="blue"
          mt={2}
          borderRadius="md"
        >
          Review & Confirm
        </Button>
      </ModalBody>

      {/* Confirmation Dialog */}
      <Modal
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        header="Confirm Cosmetics Distribution"
        footer={
          <ModalFooter gap="8px">
            <Button variant="outline" mr={3} onClick={onConfirmClose} size="sm" borderRadius="md">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={giveCosmetics.isPending}
              isDisabled={confirmationText !== 'i confirm'}
              colorScheme="red"
              size="sm"
              borderRadius="md"
            >
              Confirm Distribution
            </Button>
          </ModalFooter>
        }
      >
        <ModalBody as={Stack} gap="16px" px={0} py={4}>
          <Alert status="warning" size="sm" borderRadius="md" variant="left-accent" py={2} lineHeight="1.2">
            <AlertIcon alignSelf="flex-start" mt="1px" />
            <Box>
              <AlertTitle fontSize="sm" mb={0.5}>
                Please review carefully!
              </AlertTitle>
              <AlertDescription fontSize="xs">
                You are about to give cosmetics to multiple players. This action cannot be undone.
              </AlertDescription>
            </Box>
          </Alert>

          <FormControl>
            <SmallHeading mb={3}>You are about to give:</SmallHeading>
            <Text fontSize="sm">{formatCosmeticNames()}</Text>
          </FormControl>

          <FormControl>
            <SmallHeading mb={3}>To these {dryRunResult?.affectedPlayers?.length || 0} players:</SmallHeading>
            <Box maxH="150px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
              {dryRunResult?.affectedPlayers?.map((player: string, idx: number) => (
                <Text key={idx} fontSize="sm">
                  {player}
                </Text>
              ))}
            </Box>
          </FormControl>

          <FormControl isRequired>
            <SmallHeading mb={3}>Type "i confirm" to proceed</SmallHeading>
            <Input
              value={confirmationText}
              onChange={e => setConfirmationText(e.target.value)}
              placeholder="i confirm"
              size="sm"
              borderRadius="md"
            />
          </FormControl>
        </ModalBody>
      </Modal>
    </Modal>
  )
}
